import { useMemo } from 'react';
import * as THREE from 'three';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { Grid, OrbitControls, Sparkles, Stars } from '@react-three/drei';
import type { LogEntry } from '../types';
import ServiceNodes, { servicePositionFromIndex } from './ServiceNodes';
import LogParticleStream from './LogParticleStream';
import HolographicLogCard from './HolographicLogCard';

interface LogverseSceneProps {
  logs: LogEntry[];
  services: string[];
  selectedLog: LogEntry | null;
  activeService: string | null;
  orgSlug: string | null;
  telemetryOrigin: string;
  onSelectLog: (log: LogEntry | null) => void;
  onSelectService: (service: string | null) => void;
  timelinePosition: number;
}

export default function LogverseScene({
  logs,
  services,
  selectedLog,
  activeService,
  orgSlug,
  telemetryOrigin,
  onSelectLog,
  onSelectService,
  timelinePosition
}: LogverseSceneProps) {
  const timelineRadius = 7;

  const servicePositions = useMemo(() => {
    const map = new Map<string, THREE.Vector3Tuple>();
    services.forEach((service, index) => {
      map.set(service, servicePositionFromIndex(index, services.length, timelineRadius));
    });
    return map;
  }, [services]);

  return (
    <>
      <color attach="background" args={['#01030a']} />
      <fog attach="fog" args={['#01060f', 12, 45]} />

      <ambientLight intensity={0.24} />

      <Stars radius={90} depth={30} count={1500} factor={4} fade speed={0.4} />
      <Sparkles count={120} speed={0.12} size={2.2} opacity={0.45} color="#44f5ff" scale={[26, 8, 38]} />

      <Grid
        args={[48, 48]}
        sectionColor="#123a63"
        cellColor="#0c1f37"
        position={[0, -1.05, 0]}
        infiniteGrid
        fadeDistance={48}
        fadeStrength={1.2}
      />

      <mesh rotation={[0, 0, Math.PI / 2]} position={[0, -0.05, 0]}>
        <cylinderGeometry args={[2.2, 2.2, 36, 64, 1, true]} />
        <meshBasicMaterial color="#1f8fff" transparent opacity={0.09} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>
      <mesh rotation={[0, 0, Math.PI / 2]} position={[0, -0.05, 0]}>
        <torusGeometry args={[2.2, 0.05, 16, 140]} />
        <meshBasicMaterial color="#44f5ff" transparent opacity={0.32} toneMapped={false} />
      </mesh>

      <ServiceNodes
        services={services}
        timelineRadius={timelineRadius}
        onSelectService={onSelectService}
        activeService={activeService}
      />

      <group position={[0, 0, (1 - timelinePosition) * 12]}>
        <LogParticleStream logs={logs} servicePositions={servicePositions} onSelectLog={onSelectLog} />
      </group>

      <HolographicLogCard
        log={selectedLog}
        orgSlug={orgSlug}
        telemetryOrigin={telemetryOrigin}
        onClose={() => onSelectLog(null)}
      />

      <OrbitControls
        enablePan
        enableZoom
        enableRotate
        minDistance={5}
        maxDistance={22}
        maxPolarAngle={Math.PI * 0.62}
        target={[0, 0, 0]}
      />

      <EffectComposer>
        <Bloom mipmapBlur intensity={0.58} luminanceThreshold={0.05} luminanceSmoothing={0.35} radius={0.82} />
      </EffectComposer>
    </>
  );
}
